/**
 * Review an invitation before accepting it. (Citizen first run · slice D · S3)
 *
 * Opened from Home — the invitation card, or the Welcome moment's first step —
 * never on its own: the citizen asked to see it. It says what the company asks
 * for, asks which of their journeys to share with it — or a new one — with the
 * server's suggestion preselected (`useAcceptJourneyChooser`, R5), and accepts
 * in one click to wherever the invitation leads.
 *
 * A TestIt invitation is accepted as TESTS (D3): titled and worded as an ask
 * for results, it lists the asked tests with what is already done, asks for no
 * journey, and its button says what pressing it does.
 *
 * A citizen who has not said where they live yet is asked here, in the same
 * dialog (country and city): the company's journey opens only once that is
 * known, so accepting saves it first and the journey is live on arrival.
 *
 * Accepting is what arms the company's reads of this person's journey, so the
 * button waits for the journey choice; a failed accept shows translated copy
 * for its code, never a server message.
 */
import { Loader2 } from '@tonnie/assets';
import {
  testitAcceptBody,
  testitAcceptLabel,
  testitAcceptTitle,
  translateInviteError,
} from '@tonnie/onboarding';
import { useCountryName, useTranslation } from '@tonnie/locale';
import { useAcceptJourneyChooser, type MyPendingInvitation } from '@tonnie/sdk';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tonnie/ui';

import { ResidenceFields, useResidence } from '@/features/platform/first-run';

import { useAcceptAndRoute } from '../hooks/use-accept-and-route';
import { CompanyMark } from './CompanyMark';
import { InvitationJourneyChoice } from './InvitationJourneyChoice';

export function InvitationReviewDialog({
  invitation,
  open,
  onOpenChange,
}: {
  invitation: MyPendingInvitation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const countryName = useCountryName();
  // Asked only while the dialog is open — a card on the dashboard costs nothing.
  const chooser = useAcceptJourneyChooser(invitation.token, open);
  const { accept, isAccepting, isError, errorCode } = useAcceptAndRoute();
  const residence = useResidence();

  const company = invitation.companyName;
  const country = invitation.destinationCountry ? countryName(invitation.destinationCountry) : null;
  const isTestit = invitation.invitationKind === 'testit';
  // Every asked test already has a result: there is nothing to take, and the
  // dialog says so before the citizen accepts. Known once the preview answers.
  const testsDone = chooser.state.kind === 'tests' && chooser.state.completed;
  const title = isTestit
    ? testitAcceptTitle(t, company)
    : t('invitations.review.title', { company });
  const body = isTestit
    ? testitAcceptBody(t, { company, completed: testsDone })
    : country
      ? t('invitations.review.bodyCheckup', { company, country })
      : t('invitations.review.bodyCheckupNoCountry', { company });
  const error = isError ? translateInviteError(t, errorCode) : null;
  const busy = isAccepting || residence.isSaving;

  const onAccept = async () => {
    // Where they live first — the journey this accept creates opens on it.
    if (residence.needed && !(await residence.save())) return;
    if (!chooser.ready) return;
    // A TestIt invitation chooses no journey: none is sent.
    accept(invitation, chooser.journeyChoice);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent
        className="sm:max-w-lg"
        closeLabel={t('common.close')}
        data-testid="invitation-review"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <CompanyMark name={company} logo={invitation.companyLogo} />
            <DialogTitle className="text-left">{title}</DialogTitle>
          </div>
          <DialogDescription className="text-left pt-2">{body}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <InvitationJourneyChoice
            chooser={chooser}
            companyName={company}
            destinationCountry={invitation.destinationCountry ?? null}
            disabled={busy}
          />

          {residence.needed && (
            <section
              className="space-y-3 rounded-lg border border-border bg-muted/30 p-4"
              aria-labelledby="invitation-review-residence-title"
            >
              <div className="space-y-1">
                <h3 id="invitation-review-residence-title" className="text-sm font-semibold">
                  {t('setup.title')}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('invitations.review.residenceBody', { company })}
                </p>
              </div>
              <ResidenceFields
                residence={residence}
                idPrefix="invitation-review"
                disabled={isAccepting}
              />
            </section>
          )}

          {error && (
            <div
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-3"
              role="alert"
              data-testid="invitation-review-error"
            >
              <p className="text-sm font-medium">{error.title}</p>
              <p className="text-xs text-muted-foreground">{error.body}</p>
            </div>
          )}

          <div className="space-y-2 pt-1">
            <Button
              type="button"
              size="lg"
              className="w-full"
              disabled={!chooser.ready || busy || residence.isChecking}
              onClick={() => void onAccept()}
              data-testid="invitation-review-accept"
            >
              {busy ? (
                <>
                  <Loader2
                    className="w-4 h-4 mr-2 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  {t('invitations.review.accepting')}
                </>
              ) : isTestit ? (
                testitAcceptLabel(t, testsDone)
              ) : (
                t('invitations.review.accept')
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
